import React, { useState } from "react";
import {
  Search,
  X,
  ChevronLeft,
  ChevronRight,
  Briefcase,
  User,
  Users,
  Calendar,
  CalendarDays,
} from "lucide-react";
import { useLocation, useHistory } from "react-router-dom";
import { useAppContext } from "../../../AppContext";
import { useTranslation } from "react-i18next";
import AccountantContractorEmployeesModalWorkSlip from "./AccountantContractorEmployeesModalWorkSlip";

export default function AccountantContractorEmployees() {
  const { t } = useTranslation();
  const location = useLocation();
  const history = useHistory();
  const { contractorId, contractorName, contractorData } = location.state || {};
  const { Lang, AllData } = useAppContext();
  const isRTL = Lang === "ar" || Lang === "he";

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [showWorkSlipModal, setShowWorkSlipModal] = useState(false);

  // Use AllData from context instead of location.state
  const employees = AllData?.myEmployees || [];
  const places = AllData?.myPlaces || [];
  const dailyWork = AllData?.myDailyWork || [];

  // Debug logging
  console.log("=== AccountantContractorEmployees Debug ===");
  console.log("AllData:", AllData);
  console.log("Employees:", employees);
  console.log("Employees count:", employees.length);
  console.log("==========================================");

  const getSalaryDisplay = (item) => {
    if (item.typeSalary === 2) {
      return {
        amount: item.monthlySalary || 0,
        type: t("monthly"),
        icon: "calendar-days",
        color: "#9C27B0",
      };
    } else {
      return {
        amount: item.salary || 0,
        type: t("daily"),
        icon: "calendar",
        color: "#4CAF50",
      };
    }
  };

  // Filter and separate employees based on search query
  const getFilteredAndSeparatedEmployees = () => {
    // Don't filter by active since the data doesn't have that property
    const activeEmployees = employees || [];

    console.log("All employees count:", employees.length);
    console.log("Active employees count:", activeEmployees.length);

    let filtered = activeEmployees;
    if (searchQuery.trim()) {
      filtered = activeEmployees.filter((employee) =>
        employee.name.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Separate managers (type === 2) and employees (type !== 2)
    const managers = filtered.filter((item) => item.type === 2);
    const regularEmployees = filtered.filter((item) => item.type !== 2);

    console.log("Managers:", managers.length);
    console.log("Regular employees:", regularEmployees.length);

    return { managers, regularEmployees };
  };

  const { managers, regularEmployees } = getFilteredAndSeparatedEmployees();
  const totalCount = managers.length + regularEmployees.length;

  const handleEmployeePress = (employee) => {
    setSelectedEmployee(employee);
    setShowWorkSlipModal(true);
  };

  const renderEmployeeCard = (item) => {
    const salaryInfo = getSalaryDisplay(item);

    return (
      <div
        key={item.id}
        style={{
          backgroundColor: "#fff",
          borderRadius: "12px",
          padding: "16px",
          width: "calc(33.333% - 16px)",
          minWidth: "200px",
          margin: "8px",
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
          cursor: "pointer",
          transition: "all 0.2s",
          display: "flex",
          flexDirection: "column",
          gap: "8px",
        }}
        onClick={() => handleEmployeePress(item)}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = "translateY(-4px)";
          e.currentTarget.style.boxShadow = "0 4px 12px rgba(0, 0, 0, 0.15)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = "translateY(0)";
          e.currentTarget.style.boxShadow = "0 2px 8px rgba(0, 0, 0, 0.08)";
        }}
      >
        {/* Employee Name */}
        <h3
          style={{
            fontSize: "16px",
            fontWeight: "600",
            color: "#1a1a1a",
            margin: 0,
            textAlign: "center",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {item.name}
        </h3>

        {/* Employee ID */}
        <p
          style={{
            fontSize: "12px",
            color: "#999",
            textAlign: "center",
            margin: 0,
          }}
        >
          {t("id")}: {item.id}
        </p>

        {/* Type Badge */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "#f0f0f0",
            paddingLeft: "8px",
            paddingRight: "8px",
            paddingTop: "4px",
            paddingBottom: "4px",
            borderRadius: "8px",
            gap: "4px",
            alignSelf: "center",
          }}
        >
          {item.type === 2 ? (
            <Briefcase size={12} color="#FF9800" />
          ) : (
            <User size={12} color="#2196F3" />
          )}
          <span
            style={{
              fontSize: "11px",
              fontWeight: "600",
              color: item.type === 2 ? "#FF9800" : "#2196F3",
            }}
          >
            {item.type === 2 ? t("manager") : t("employee")}
          </span>
        </div>

        {/* Divider */}
        <div
          style={{
            height: "1px",
            backgroundColor: "#f0f0f0",
            margin: "8px 0",
          }}
        />

        {/* Salary Details */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "6px",
            alignItems: "center",
          }}
        >
          {/* Salary Type */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "4px",
            }}
          >
            {salaryInfo.icon === "calendar-days" ? (
              <CalendarDays size={16} color={salaryInfo.color} />
            ) : (
              <Calendar size={16} color={salaryInfo.color} />
            )}
            <span
              style={{
                fontSize: "12px",
                color: "#666",
              }}
            >
              {salaryInfo.type}
            </span>
          </div>

          {/* Salary Amount */}
          <div
            style={{
              fontSize: "16px",
              fontWeight: "700",
              color: "#111827",
            }}
          >
            ₪{salaryInfo.amount.toLocaleString()}
          </div>
        </div>
      </div>
    );
  };

  const renderSectionHeader = (title, count, iconName) => (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "12px 8px",
        marginBottom: "12px",
        flexDirection: isRTL ? "row-reverse" : "row",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "12px",
          flexDirection: isRTL ? "row-reverse" : "row",
        }}
      >
        {iconName === "briefcase" ? (
          <Briefcase size={20} color="#6200EA" />
        ) : (
          <Users size={20} color="#6200EA" />
        )}
        <h2
          style={{
            fontSize: "20px",
            fontWeight: "700",
            color: "#1a1a1a",
            margin: 0,
          }}
        >
          {title}
        </h2>
      </div>
      <div
        style={{
          backgroundColor: "#6200EA",
          paddingLeft: "12px",
          paddingRight: "12px",
          paddingTop: "6px",
          paddingBottom: "6px",
          borderRadius: "12px",
          minWidth: "32px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <span
          style={{
            color: "#fff",
            fontSize: "14px",
            fontWeight: "600",
          }}
        >
          {count}
        </span>
      </div>
    </div>
  );

  const renderManagersList = () => {
    if (managers.length === 0) return null;

    return (
      <div style={{ marginBottom: "32px" }}>
        {renderSectionHeader(t("managers"), managers.length, "briefcase")}
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            margin: "-8px",
            flexDirection: isRTL ? "row-reverse" : "row",
            justifyContent: isRTL ? "flex-end" : "flex-start",
          }}
        >
          {managers.map((item) => renderEmployeeCard(item))}
        </div>
      </div>
    );
  };

  const renderEmployeesList = () => {
    if (regularEmployees.length === 0) return null;

    return (
      <div style={{ marginBottom: "32px" }}>
        {renderSectionHeader(t("employees"), regularEmployees.length, "people")}
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            margin: "-8px",
            flexDirection: isRTL ? "row-reverse" : "row",
            justifyContent: isRTL ? "flex-end" : "flex-start",
          }}
        >
          {regularEmployees.map((item) => renderEmployeeCard(item))}
        </div>
      </div>
    );
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        backgroundColor: "#f5f5f5",
        direction: isRTL ? "rtl" : "ltr",
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          backgroundColor: "#FFFFFF",
          padding: "20px",
          borderBottom: "1px solid #E5E7EB",
          position: "sticky",
          top: 0,
          zIndex: 100,
          flexDirection: isRTL ? "row-reverse" : "row",
        }}
      >
        <button
          onClick={() => history.goBack()}
          style={{
            width: "44px",
            height: "44px",
            borderRadius: "22px",
            backgroundColor: "#F3F4F6",
            border: "none",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            cursor: "pointer",
            marginRight: isRTL ? 0 : "16px",
            marginLeft: isRTL ? "16px" : 0,
            transition: "all 0.2s",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = "#E5E7EB";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = "#F3F4F6";
          }}
        >
          {isRTL ? (
            <ChevronRight size={24} color="#1F2937" />
          ) : (
            <ChevronLeft size={24} color="#1F2937" />
          )}
        </button>
        <div style={{ flex: 1 }}>
          <h1
            style={{
              fontSize: "24px",
              fontWeight: "700",
              color: "#1a1a1a",
              margin: 0,
              marginBottom: "4px",
              textAlign: isRTL ? "right" : "left",
            }}
          >
            {contractorName || t("contractor")}
          </h1>
          <p
            style={{
              fontSize: "14px",
              color: "#666",
              margin: 0,
              textAlign: isRTL ? "right" : "left",
            }}
          >
            {totalCount} {totalCount === 1 ? t("employee") : t("employees")}
          </p>
        </div>
      </div>

      {/* Search Bar */}
      <div
        style={{
          padding: "16px 20px",
          backgroundColor: "#f5f5f5",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            backgroundColor: "#fff",
            paddingLeft: "16px",
            paddingRight: "16px",
            paddingTop: "12px",
            paddingBottom: "12px",
            borderRadius: "12px",
            boxShadow: "0 1px 4px rgba(0, 0, 0, 0.05)",
            maxWidth: "600px",
            flexDirection: isRTL ? "row-reverse" : "row",
          }}
        >
          <Search
            size={20}
            color="#999"
            style={{
              marginRight: isRTL ? 0 : "12px",
              marginLeft: isRTL ? "12px" : 0,
            }}
          />
          <input
            type="text"
            placeholder={t("searchByName")}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              flex: 1,
              fontSize: "15px",
              color: "#1a1a1a",
              border: "none",
              outline: "none",
              backgroundColor: "transparent",
              textAlign: isRTL ? "right" : "left",
            }}
          />
          {searchQuery.length > 0 && (
            <button
              onClick={() => setSearchQuery("")}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: "4px",
                display: "flex",
                alignItems: "center",
                marginLeft: isRTL ? 0 : "8px",
                marginRight: isRTL ? "8px" : 0,
              }}
            >
              <X size={20} color="#999" />
            </button>
          )}
        </div>
      </div>

      {/* Employee List */}
      {totalCount === 0 ? (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: "80px 20px",
          }}
        >
          <Users size={64} color="#ccc" />
          <p
            style={{
              fontSize: "18px",
              color: "#999",
              marginTop: "16px",
              marginBottom: "8px",
            }}
          >
            {searchQuery ? t("noEmployeesMatchSearch") : t("noEmployeesFound")}
          </p>
          <p
            style={{
              fontSize: "14px",
              color: "#ccc",
              margin: 0,
            }}
          >
            {searchQuery ? t("tryDifferentSearch") : t("pullDownToRefresh")}
          </p>
        </div>
      ) : (
        <div
          style={{
            padding: "20px",
            maxWidth: "1400px",
            margin: "0 auto",
          }}
        >
          {renderManagersList()}
          {renderEmployeesList()}
        </div>
      )}

      {/* Work Slip Modal */}
      {showWorkSlipModal && (
        <AccountantContractorEmployeesModalWorkSlip
          visible={showWorkSlipModal}
          onClose={() => {
            setShowWorkSlipModal(false);
            setSelectedEmployee(null);
          }}
          employee={selectedEmployee}
          contractorName={contractorName}
          places={places}
          dailyWork={dailyWork}
          allEmployees={employees}
        />
      )}
    </div>
  );
}
